import React, { useEffect, useMemo, useRef, useState } from "react";
import UnitRow from "./UnitRow";
import BulkApplyRow from "./BulkApplyRow";
import { buildDraft } from "./itemDraftHelpers";

// Defensive parse: blank/undefined/non-numeric -> null (not 0 or NaN), so a
// blank HUs box or a not-yet-loaded item field never gets treated as a real
// value on either side of the preview comparison below.
function toIntOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
}

// Shared empty Set/object/array so the "no preview" case is referentially
// stable — the prune effect in ItemCard depends on `preview.removedIds`, and
// a fresh `new Set()`/`{}`/`[]` per render would make that effect (and any
// memoized consumer of the other empty fields) fire every render.
const EMPTY_ID_SET = new Set();
const EMPTY_UNIT_PREVIEWS = {};
const EMPTY_NEW_UNIT_PREVIEWS = [];

export const EMPTY_UNITS_PREVIEW = {
  isDirty: false,
  addCount: 0,
  removeCount: 0,
  removedIds: EMPTY_ID_SET,
  displayCount: null,
  previewActive: false,
  draftHandlingUnits: null,
  draftItemQuantity: null,
  unitPreviewsById: EMPTY_UNIT_PREVIEWS,
  newUnitPreviews: EMPTY_NEW_UNIT_PREVIEWS,
};

// ---------------------------------------------------------------------------
// Wave A (mockup parity, docs/order-item-units-frontend-plan-2): live
// per-unit value derivation. Every formula below is a line-for-line mirror of
// OrderItemUnitSyncService#apply_defaults (app/services/order_item_unit_sync_service.rb)
// so the preview NEVER "changes" what Save's own re-derivation produces — the
// file:line comment on each function is the server counterpart it mirrors.
// Pure display math: no state, nothing persisted, nothing PATCHed.
// ---------------------------------------------------------------------------

const LB_TO_KG = 0.453592;

// Mirrors itemDraftHelpers.js's private toLb / OrderItemUnit#convert_to_lb:
// only 'kg'/'kgs' are kilograms, everything else (including blank) is already
// pounds. Duplicated locally rather than imported since itemDraftHelpers
// doesn't export it (same "small private duplicate" convention UnitRow/
// BulkApplyRow already use for their own shared bits).
function toLb(value, uom) {
  const num = parseFloat(value) || 0;
  return ["kg", "kgs"].includes(String(uom || "").toLowerCase()) ? num / LB_TO_KG : num;
}

function round2(n) {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

// Mirrors OrderItemUnitSyncService#even_splits
// (app/services/order_item_unit_sync_service.rb:181-186): base = total/count
// floor, remainder distributed to the FIRST `remainder` units.
function evenSplits(total, count) {
  if (!count || count <= 0) return [];
  const t = Math.max(parseInt(total, 10) || 0, 0);
  const base = Math.floor(t / count);
  const remainder = t - base * count;
  return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0));
}

// Mirrors OrderItemUnitSyncService#normalized_dim_uom
// (app/services/order_item_unit_sync_service.rb:211-213).
function normalizedDimUom(uom) {
  return ["cm", "cms"].includes(String(uom || "").toLowerCase()) ? "cm" : "in";
}

// Mirrors OrderItemUnitSyncService#desired_count's bounds
// (order_item_unit_sync_service.rb:72-86): floor at 0 (`[count, 0].max` —
// handling_units of 0 legitimately means zero scannable units, user decision
// 2026-08-10, so typing HUs=0 previews "all rows removed", matching what Save
// does) and clamp at MAX_UNITS_PER_ITEM = 1_000 (the service's IMPORTANT-5
// fan-out ceiling — typing 5000 must not preview 5000 placeholder rows Save
// will never mint). Single source: every count the preview derives (row diff,
// displayCount, split/weight divisors) flows through this.
const MAX_UNITS_PER_ITEM = 1000;
function boundedDesiredCount(count) {
  return Math.min(Math.max(count, 0), MAX_UNITS_PER_ITEM);
}

// Numeric-aware compare (mirrors UnitRow's numsEqual / itemDraftHelpers'
// NUMERIC_DRAFT_FIELDS convention): "40" vs "40.00" is not a change.
function numEqual(a, b) {
  const aBlank = a === "" || a === null || a === undefined;
  const bBlank = b === "" || b === null || b === undefined;
  if (aBlank && bBlank) return true;
  if (aBlank !== bBlank) return false;
  const na = parseFloat(a);
  const nb = parseFloat(b);
  return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
}

// Task 3's dirty gate: true when the item draft differs from its persisted
// baseline on ANY field the server's Save-time derivation actually reads —
// item_quantity/handling_units (piece split + unit count), the weight total
// or uom (booked weight, since normalizing to lb depends on both), the dims
// or dim uom (booked dims), or wh_dock (M1: item-level Storage Location maps
// to legacy wh_dock, full replace). A change to item_name/model/serial/
// piece_type/handling_unit_type never gates the preview: Save wouldn't
// recompute anything below from those fields.
const RELEVANT_NUMERIC_FIELDS = [
  "item_quantity",
  "handling_units",
  "weight_total_exact",
  "item_length",
  "item_width",
  "item_height",
];
const RELEVANT_STRING_FIELDS = ["item_weight_uom", "item_length_uom", "wh_dock"];

function relevantFieldsDirty(draft, baseline) {
  if (!draft || !baseline) return false;
  if (RELEVANT_NUMERIC_FIELDS.some((f) => !numEqual(draft[f], baseline[f]))) return true;
  return RELEVANT_STRING_FIELDS.some((f) => String(draft[f] || "") !== String(baseline[f] || ""));
}

// THE single source of the live per-item preview derivation (Frontend Task
// 11, extended by Wave A): computed once in ItemCard — which owns
// checkedIds and must prune/exclude preview-removed units from selection —
// and passed down to this table (and, for the draft-aware split lock, on
// into UnitRow/BulkApplyRow), so nothing below can ever disagree on what
// Save is about to do.
//
// - `isDirty`/`addCount`/`removeCount`/`removedIds`/`displayCount` are
//   UNCHANGED from Frontend Task 11: purely the HUs-count preview (draft HUs
//   vs. the real persisted row count), gating only the add/remove ROWS.
//   - `isDirty` gates that half: the draft HUs must parse to a real number
//     AND differ from the item's persisted handling_units — a blank box, an
//     unparseable value, or a read-only table never previews.
//   - The diff is taken against `units.length` (the REAL persisted row
//     count), not against item.handling_units again: those two are normally
//     kept in lockstep by the server's sync-on-save, but only the actual row
//     count determines how many placeholder/removed rows make sense to show.
//   - `removedIds`: units arrive ascending by seq (asc(:seq) in the index
//     action) and the server's OrderItemUnitSyncService#sync_count always
//     soft-deletes the tail (highest-seq/most-recently-created units) when
//     the desired count shrinks, so the last `removeCount` ids of the
//     already-ordered array are exactly the set to mark "removed on Save".
// - `previewActive` (Wave A Fix 3) is the BROADER dirty gate for per-cell
//   VALUE previews (piece split/weight/dims/location): true whenever the
//   draft differs from its persisted baseline on any field
//   relevantFieldsDirty checks, independent of whether the HUs count itself
//   changed. UnitRow additionally gates display on `mode === "booked"` —
//   Re-Weighed columns are actual/manual measurements, never derived.
// - `draftHandlingUnits`/`draftItemQuantity` (Wave A Fix 4): the draft's own
//   parsed HUs/pieces, exposed so UnitRow/BulkApplyRow can derive their
//   piece-split lock from what the user is TYPING while the box is
//   editable — `null` (not merely absent) whenever readOnly, so those
//   consumers fall back to item.* unconditionally rather than accidentally
//   locking/unlocking off a stale number.
// - `unitPreviewsById`/`newUnitPreviews`: the actual computed values, keyed
//   by real unit id (existing rows) or by placeholder index (rows the
//   server will create on Save) — see apply_piece_splits/apply_booked_defaults/
//   apply_location_defaults mirrors below.
// Units-during-create fix (docs/order-item-units-frontend-plan-2): pulled out
// of computeHandlingUnitsPreview's `previewActive` branch so the exact same
// split/weight/dims/location formulas can seed a brand-new pending item's
// table (computeNewItemUnitsPreview below) without a second copy of the
// server-mirroring math. Depends only on the draft + how many rows are
// visible — never on the real `units` array — so it's equally valid whether
// those rows are existing/placeholder (an item being edited) or entirely
// synthetic (an item that doesn't exist server-side yet).
function buildUnitPreviewFormulas(draft, visibleCount) {
  const pieces = Math.max(parseInt(draft.item_quantity, 10) || 0, 0);

  // Mirrors OrderItemUnitSyncService#apply_piece_splits' `locked` gate
  // (order_item_unit_sync_service.rb:166-168): desired_count.positive? &&
  // desired_count == pieces -> every unit's split previews as 1.
  const splitLockedPreview = visibleCount > 0 && visibleCount === pieces;
  const splits = evenSplits(pieces, visibleCount);

  // Mirrors OrderItemUnitSyncService#apply_booked_defaults
  // (order_item_unit_sync_service.rb:188-209): total normalized to lb via
  // item_total_weight_lb (sync service:57-64), evenly divided, remainder
  // absorbed by the LAST unit so the sum always reconciles exactly.
  //
  // Wave B review CRITICAL: item_total_weight_lb ROUNDS the lb total to 2dp
  // BEFORE the division (sync service:60/62 `.round(2)`), and dividing the
  // unrounded total genuinely diverges (e.g. 0.75 kg over 6 units: server
  // rounds to 1.65 lb first -> per-unit 0.27, last 0.30; dividing the
  // unrounded 1.65347 lb previewed 0.28, last 0.25). The preview must
  // round first, exactly like the server — this rounded total is also what
  // computeLiveMismatches compares against, so the mismatch sum and these
  // per-unit values stay in lockstep by construction.
  const totalLb = round2(toLb(draft.weight_total_exact, draft.item_weight_uom));
  const perUnit = visibleCount > 0 ? round2(totalLb / visibleCount) : 0;
  const weights = Array.from({ length: visibleCount }, () => perUnit);
  if (visibleCount > 0) weights[visibleCount - 1] = round2(totalLb - perUnit * (visibleCount - 1));

  // Mirrors the `dims` hash in apply_booked_defaults (sync service:193-194)
  // — one L/W/H/uom triple applied identically to every non-manual unit,
  // not index-dependent like splits/weights.
  const dims = {
    length: draft.item_length,
    width: draft.item_width,
    height: draft.item_height,
    dimUom: normalizedDimUom(draft.item_length_uom),
  };

  // Mirrors apply_location_defaults (sync service:215-222): `return if
  // location.blank?` — a blank item wh_dock (M1: item-level Storage Location
  // maps to legacy wh_dock) leaves EVERY unit's location alone (no
  // override), so `storageLocation: null` here must mean "don't preview
  // this field", not "preview an empty string".
  const location = (draft.wh_dock || "").trim();
  const hasLocation = location !== "";

  return (i) => ({
    pieceSplit: splitLockedPreview ? 1 : splits[i] != null ? splits[i] : 0,
    weight: weights[i] != null ? weights[i] : 0,
    weightUom: "lb",
    length: dims.length,
    width: dims.width,
    height: dims.height,
    dimUom: dims.dimUom,
    storageLocation: hasLocation ? location : null,
  });
}

export function computeHandlingUnitsPreview({ units, draft, item, readOnly, pendingEdits = {} }) {
  const list = units || [];
  const draftHUs = toIntOrNull(draft && draft.handling_units);
  const persistedHUsRaw = toIntOrNull(item && item.handling_units);
  // B1 root cause: a legacy item that has never had handling_units set at
  // all (nil — no backfill/sync has ever run for it, so it also has zero
  // OrderItemUnits) made `persistedHUsRaw` null forever, which made isDirty
  // below permanently false NO MATTER WHAT the user typed into the HUs box —
  // buildDraft's own fallback (source.handling_units ?? 1) only seeds the
  // DRAFT's starting value, it never reaches item.handling_units itself.
  // With isDirty stuck false, addCount/removedIds/displayCount never
  // move off their zero defaults, so on top of `units` already being `[]`
  // for such an item, NO unit rows — real or placeholder — ever rendered,
  // regardless of what HUs value was typed (the reported "editing HUs on a
  // legacy order with no units shows no unit boxes" bug). Falling back to
  // the item's ACTUAL current unit count (list.length, typically 0 for
  // exactly this item) as the baseline instead means any typed HUs value
  // differing from that baseline is correctly recognized as dirty, so
  // desiredCount/addCount below produce real placeholder rows the user can
  // fill in and Save then creates server-side (OrderItem#sync_scannable_units
  // -> OrderItemUnitSyncService#sync_count grows from 0 to the saved
  // handling_units, exactly like any other HUs increase). Items that already
  // carry a real persisted handling_units value are unaffected.
  const persistedHUs = persistedHUsRaw !== null ? persistedHUsRaw : list.length;
  const isDirty = !readOnly && draftHUs !== null && persistedHUs !== null && draftHUs !== persistedHUs;
  // Wave B review IMPORTANT-1: the row-count preview must run the typed HUs
  // through the server's own desired_count bounds (see boundedDesiredCount
  // above) — otherwise HUs=0 previews every row as "removed on Save" while
  // Save actually keeps one, and HUs>1000 previews an uncapped placeholder
  // flood the server will clamp.
  const desiredCount = isDirty ? boundedDesiredCount(draftHUs) : list.length;
  const diff = isDirty ? desiredCount - list.length : 0;
  const addCount = diff > 0 ? diff : 0;
  const removeCount = diff < 0 ? -diff : 0;
  const removedIds =
    removeCount > 0 ? new Set(list.slice(list.length - removeCount).map((u) => u.id)) : EMPTY_ID_SET;
  const displayCount = desiredCount;

  const draftHandlingUnits = readOnly ? null : draftHUs;
  const draftItemQuantity = readOnly ? null : toIntOrNull(draft && draft.item_quantity);

  const baseline = item ? buildDraft(item) : null;
  const previewActive = !readOnly && !!draft && relevantFieldsDirty(draft, baseline);

  let unitPreviewsById = EMPTY_UNIT_PREVIEWS;
  let newUnitPreviews = EMPTY_NEW_UNIT_PREVIEWS;

  // Audit fix C1: previously gated purely on `previewActive`. A legacy item
  // with handling_units nil and zero real units (never backfilled) makes
  // `isDirty`/`addCount` above fire off the list.length=0 fallback (see the
  // B1 comment on persistedHUs) even on a completely UNTOUCHED card — draft
  // === buildDraft(item), so relevantFieldsDirty (and therefore
  // previewActive) correctly reports "nothing changed". With the old
  // `if (previewActive)` guard, that left addCount > 0 placeholder rows
  // rendering with NO seeded split/weight/dims — computeLiveMismatches then
  // summed 0 against the real piece/weight totals and permanently blocked
  // Save on an item nobody touched. Building the previews whenever there are
  // ANY placeholder rows to seed (addCount > 0), not just when the draft has
  // actively diverged, keeps the seed values (and therefore the live
  // mismatch sums) in lockstep with what Save's own sync_count + apply_*
  // would produce for that same row count.
  if (previewActive || addCount > 0) {
    // The visible row count for the split/weight formulas is exactly the
    // count of rows this table is about to render (existing rows minus
    // preview-removed ones, plus placeholder rows) — i.e. `displayCount`
    // above — so a per-row preview value can never be computed against a
    // unit count different from what's on screen.
    const visibleCount = displayCount;
    const buildOneUnitPreview = buildUnitPreviewFormulas(draft, visibleCount);

    unitPreviewsById = {};
    list.forEach((unit, i) => {
      // A preview-removed row already renders its "removed on Save" state;
      // it never had a real formula value to preview in the first place.
      if (removedIds.has(unit.id)) return;
      unitPreviewsById[unit.id] = buildOneUnitPreview(i);
    });

    newUnitPreviews = Array.from({ length: addCount }, (_, k) => buildOneUnitPreview(list.length + k));

    // Manual-aware piece splits (mirror of the server's apply_piece_splits):
    // a unit's split is FIXED when it's hand-edited (persisted manual) OR is
    // carrying an in-flight pending split edit — both hold their value, and the
    // REMAINDER is distributed evenly across the auto (non-fixed + new) units.
    // Counting pending edits as fixed keeps the live preview correct while the
    // user edits a unit AND the item total in the same session, before either
    // is saved (and the manual flag persisted).
    const pieces = Math.max(parseInt(draft.item_quantity, 10) || 0, 0);
    const survivors = list.filter((u) => !removedIds.has(u.id));
    const splitPending = (u) => pendingEdits[u.id] && pendingEdits[u.id].piece_split !== undefined;
    const splitFixed = (u) => u.piece_split_manual || splitPending(u);
    const splitFixedVal = (u) =>
      parseInt(splitPending(u) ? pendingEdits[u.id].piece_split : u.piece_split, 10) || 0;
    const splitManualTotal = survivors.filter(splitFixed).reduce((s, u) => s + splitFixedVal(u), 0);
    const splitAutoCount = survivors.filter((u) => !splitFixed(u)).length + addCount;
    const autoSplits = evenSplits(Math.max(pieces - splitManualTotal, 0), splitAutoCount);
    let ai = 0;
    survivors.forEach((u) => {
      if (splitFixed(u)) return;
      if (unitPreviewsById[u.id]) {
        unitPreviewsById[u.id] = { ...unitPreviewsById[u.id], pieceSplit: autoSplits[ai] || 0 };
      }
      ai += 1;
    });
    newUnitPreviews = newUnitPreviews.map((p) => {
      const next = { ...p, pieceSplit: autoSplits[ai] || 0 };
      ai += 1;
      return next;
    });

    // Manual-aware booked weight (mirror of apply_booked_defaults): same "fixed
    // = manual OR pending" rule; the item weight not claimed by fixed units is
    // distributed evenly across the auto units (remainder on the last), so the
    // booked-weight sum stays on the item total. This is the user's scenario:
    // 5 HUs @ 25lb (5 each) -> set unit 1 = 10 and item total = 15 -> unit 1
    // stays 10, remaining 5 splits 1.25 across the other four, live.
    const totalLb = round2(toLb(draft.weight_total_exact, draft.item_weight_uom));
    const wPending = (u) => pendingEdits[u.id] && pendingEdits[u.id].booked_weight !== undefined;
    const wFixed = (u) => u.booked_weight_manual || wPending(u);
    const wFixedLb = (u) => {
      const ov = pendingEdits[u.id] || {};
      const w = wPending(u) ? ov.booked_weight : u.booked_weight;
      const uom = ov.booked_weight_uom !== undefined ? ov.booked_weight_uom : u.booked_weight_uom;
      return toLb(w, uom);
    };
    const wManualLb = survivors.filter(wFixed).reduce((s, u) => s + wFixedLb(u), 0);
    const wAutoCount = survivors.filter((u) => !wFixed(u)).length + addCount;
    const remainingLb = Math.max(round2(totalLb - wManualLb), 0);
    const perW = wAutoCount > 0 ? round2(remainingLb / wAutoCount) : 0;
    const weightAt = (idx) => (idx === wAutoCount - 1 ? round2(remainingLb - perW * (wAutoCount - 1)) : perW);
    let wi = 0;
    survivors.forEach((u) => {
      if (wFixed(u)) return;
      if (unitPreviewsById[u.id]) {
        unitPreviewsById[u.id] = { ...unitPreviewsById[u.id], weight: weightAt(wi), weightUom: "lb" };
      }
      wi += 1;
    });
    newUnitPreviews = newUnitPreviews.map((p) => {
      const next = { ...p, weight: weightAt(wi), weightUom: "lb" };
      wi += 1;
      return next;
    });
  }

  return {
    isDirty,
    addCount,
    removeCount,
    removedIds,
    displayCount,
    previewActive,
    draftHandlingUnits,
    draftItemQuantity,
    unitPreviewsById,
    newUnitPreviews,
  };
}

// Units-during-create fix (docs/order-item-units-frontend-plan-2): the
// preview for a pending "Add N Items" card that has no server-side item at
// all yet — reported bug was "after creation of item scannable units are
// popping, but while creating the item it should work". Unlike
// computeHandlingUnitsPreview above (which gates every value preview on the
// draft having DIVERGED from a persisted baseline), a brand-new item has no
// baseline to diverge from — every seed row is inherently a preview of what
// Save is about to create, so this is unconditionally "active" rather than
// dirty-gated. `units` is always `[]` for a pending item (nothing persisted
// yet), so every seed row is an "added" row — there is no unitPreviewsById
// half at all, only newUnitPreviews. Reuses the exact same
// buildUnitPreviewFormulas as the edit-flow so a brand-new item's seed
// values and an existing item's placeholder-row values can never diverge on
// formula.
export function computeNewItemUnitsPreview({ draft, readOnly }) {
  const draftHUs = toIntOrNull(draft && draft.handling_units);
  // Same floor-1/cap-1000 bounds as the edit-flow preview (boundedDesiredCount
  // above) — a blank/zero HUs box still seeds one row (an item always keeps
  // at least one unit), and a huge typed value can't flood the table with
  // more placeholder rows than Save will ever actually create.
  const count = boundedDesiredCount(draftHUs !== null ? draftHUs : 1);
  const buildOneUnitPreview = buildUnitPreviewFormulas(draft, count);
  const newUnitPreviews = Array.from({ length: count }, (_, i) => buildOneUnitPreview(i));

  return {
    isDirty: true,
    addCount: count,
    removeCount: 0,
    removedIds: EMPTY_ID_SET,
    displayCount: count,
    previewActive: true,
    // Same readOnly -> null convention as computeHandlingUnitsPreview's own
    // draftHandlingUnits/draftItemQuantity (Wave A Fix 4), for hygiene —
    // neither field has a live consumer today, but a future one shouldn't
    // read a "live typed value" out of a locked table.
    draftHandlingUnits: readOnly ? null : draftHUs,
    draftItemQuantity: readOnly ? null : toIntOrNull(draft && draft.item_quantity),
    unitPreviewsById: EMPTY_UNIT_PREVIEWS,
    newUnitPreviews,
  };
}

// ---------------------------------------------------------------------------
// B2 (mockup-parity, docs/order-item-units-frontend-plan-2): live pre-Save
// mismatch computation. ItemCard used to render `item.mismatches` straight
// from the last server GET, so typing e.g. Pieces=20 over a saved 10 showed
// no warning until Save. This mirrors OrderItemUnitSyncService.mismatches
// (app/services/order_item_unit_sync_service.rb:37-54) exactly, formula for
// formula, but reads the DRAFT + this same preview object instead of the
// last-loaded units/item — one computation, reused by both the value
// previews above and the mismatch chips/panel in ItemCard, so they can never
// disagree about what Save is about to produce.
//
//   piece_split_mismatch: pieces = draft.item_quantity (server:
//     order_item.item_quantity.to_i); split_sum = sum over every unit that
//     will exist after Save of that unit's piece_split (server: sums the
//     PERSISTED piece_split on every unit — which is exactly the preview
//     value for a non-manual unit, since Save's own apply_piece_splits just
//     wrote that same formula onto it — or the persisted value verbatim for
//     a manual unit/whenever the preview isn't active).
//   weight_mismatch: total_lb = draft's weight total normalized to lb AND
//     rounded to 2dp (server: OrderItemUnitSyncService.item_total_weight_lb);
//     weight_sum = the same per-unit sum/rounding rule as split_sum above,
//     but for booked weight, compared with a WEIGHT_TOLERANCE of 0.01
//     (server: same constant, order_item_unit_sync_service.rb:8). Non-manual
//     units contribute `p.weight` straight from computeHandlingUnitsPreview's
//     rounded per-unit weights array (which itself divides the ROUNDED total,
//     per the CRITICAL note there) — the sum and the per-cell previews are
//     the same numbers by construction, never two parallel derivations.
//
// `preview` may be the EMPTY_UNITS_PREVIEW default (previewActive false) —
// every unit then simply contributes its own persisted piece_split/
// booked_weight, i.e. this collapses to exactly the server's own mismatches
// formula when the draft isn't dirty on anything Save would recompute.
const WEIGHT_TOLERANCE = 0.01;

// Ruby's Float#to_s always shows at least one decimal (90.0, not 90) when a
// mismatch message string-interpolates a rounded total; mirrored here so a
// live message reads identically to the one the server would send once
// this exact state gets saved.
function rubyFloatString(n) {
  const rounded = round2(n);
  return Number.isInteger(rounded) ? `${rounded}.0` : `${rounded}`;
}

// Units-during-create fix: `pendingEdits` is this item's own container-owned
// override bucket ({existing: {unitId: {field: value}}, added: {index:
// {field: value}}}) — the SAME map UnitRow's deferred cells write into via
// onPendingEdit (see UnitRow's useDeferred branch / ItemsMeasureUnits'
// setUnitPendingEdit) while an edit hasn't reached the server yet. Before
// this fix, a user-typed weight sitting in that map was invisible to this
// mismatch sum — it kept reading the stale persisted/derived value straight
// off `units`/`preview` instead, so editing an added row's weight (or an
// existing row's, while previewing) couldn't ever change the live
// weight_mismatch chip. Resolved here, once, so the sum this chip shows and
// the value the cell displays can never disagree.
function overriddenWeightLb(override, fallbackLb) {
  if (!override || override.booked_weight === undefined) return fallbackLb;
  return toLb(override.booked_weight, override.booked_weight_uom);
}

// Same resolution rule for a unit's piece split: an in-flight pending edit
// wins, else the caller's fallback (manual persisted value, or the derived
// preview value for non-manual units).
function overriddenSplit(override, fallback) {
  if (!override || override.piece_split === undefined || override.piece_split === "") return fallback;
  const n = parseInt(override.piece_split, 10);
  return Number.isNaN(n) ? fallback : n;
}

export function computeLiveMismatches({ units, draft, preview, pendingEdits }) {
  if (!draft) return [];
  const list = units || [];
  const removedIds = (preview && preview.removedIds) || EMPTY_ID_SET;
  const survivors = removedIds.size > 0 ? list.filter((u) => !removedIds.has(u.id)) : list;
  const unitPreviewsById = (preview && preview.unitPreviewsById) || EMPTY_UNIT_PREVIEWS;
  const newUnitPreviews = (preview && preview.newUnitPreviews) || EMPTY_NEW_UNIT_PREVIEWS;
  const existingOverrides = (pendingEdits && pendingEdits.existing) || EMPTY_UNIT_PREVIEWS;
  const addedOverrides = (pendingEdits && pendingEdits.added) || EMPTY_UNIT_PREVIEWS;

  // Audit fix C1 (belt-and-braces): mirrors OrderItemUnitSyncService
  // .mismatches' own `return [] if units.empty?` (order_item_unit_sync_
  // service.rb:39). Once the previewActive||addCount>0 widening above is in
  // place this is normally moot (any placeholder rows worth checking already
  // populate newUnitPreviews), but a card with zero real units and nothing
  // being previewed/seeded at all (e.g. a blank/unparseable HUs draft, see
  // computeHandlingUnitsPreview's `isDirty` gate) has nothing for Save to
  // check either — same "nothing to report" as the server.
  //
  // Uses `survivors` (rows left AFTER preview removals), not raw `list`, so
  // HUs=0 — which marks every existing unit removed and seeds no new rows —
  // also reports no mismatch: with zero resulting units there is nothing to
  // split pieces into or hold weight, mirroring the server's post-sync
  // `return [] if units.empty?`. Otherwise a 0-unit item with pieces > 0 would
  // raise a piece-split "0 of N" blocker and wrongly block Save.
  if (survivors.length === 0 && newUnitPreviews.length === 0) return [];

  const result = [];

  // Piece-split mismatch check (mirrors OrderItemUnitSyncService.mismatches):
  // since the manual-split guard was restored (a hand-edited split is NEVER
  // auto-overridden by pieces/HUs changes), manual splits can genuinely
  // disagree with the piece total — the mockup's "Piece split doesn't match
  // the total pieces" blocker. Non-manual units use their derived preview
  // value (always sums correctly by construction); manual units use their
  // real value; in-flight pending edits win over both.
  const pieces = Math.max(parseInt(draft.item_quantity, 10) || 0, 0);
  const splitSum =
    survivors.reduce((sum, u) => {
      const fallback = u.piece_split_manual
        ? parseInt(u.piece_split, 10) || 0
        : (() => {
            const p = unitPreviewsById[u.id];
            return p ? p.pieceSplit || 0 : parseInt(u.piece_split, 10) || 0;
          })();
      return sum + overriddenSplit(existingOverrides[u.id], fallback);
    }, 0) +
    newUnitPreviews.reduce((sum, p, i) => sum + overriddenSplit(addedOverrides[i], p.pieceSplit || 0), 0);

  if (splitSum !== pieces) {
    result.push({
      code: "piece_split_mismatch",
      expected: pieces,
      actual: splitSum,
      message: `Piece split adds up to ${splitSum} of ${pieces} pieces`,
    });
  }

  const totalLb = round2(toLb(draft.weight_total_exact, draft.item_weight_uom));
  const weightSum = round2(
    survivors.reduce((sum, u) => {
      const fallbackLb = u.booked_weight_manual
        ? toLb(u.booked_weight, u.booked_weight_uom)
        : (() => {
            const p = unitPreviewsById[u.id];
            return p ? p.weight : toLb(u.booked_weight, u.booked_weight_uom);
          })();
      return sum + overriddenWeightLb(existingOverrides[u.id], fallbackLb);
    }, 0) +
      newUnitPreviews.reduce((sum, p, i) => sum + overriddenWeightLb(addedOverrides[i], p.weight || 0), 0)
  );

  if (Math.abs(weightSum - totalLb) > WEIGHT_TOLERANCE) {
    result.push({
      code: "weight_mismatch",
      expected: totalLb,
      actual: weightSum,
      message: `Unit weights add up to ${rubyFloatString(weightSum)} of ${rubyFloatString(totalLb)} lb`,
    });
  }

  return result;
}

// Editable-units fix (docs/order-item-units-frontend-plan-2): a placeholder
// row for a not-yet-created unit used to be static text (see removed
// NewUnitPreviewRow) with every cell disabled — so the moment a user raised
// Handling Units they had no way to actually fill in the new rows before
// Save, and existing rows simultaneously locked into a disabled "Recalculated
// on Save" state. That's the reported bug. The fix: an added row is now a
// REAL UnitRow rendered in `isAdded` mode — same component existing rows use,
// pre-filled with this index's computed preview default (`buildOneUnitPreview`
// via `preview.newUnitPreviews[i]`, identical formulas as before) but fully
// editable. `seq` is only a best-effort DISPLAY label ("Unit N") — the item's
// own units are numbered off the ORDER-wide seq counter server-side
// (order_item_unit_sync_service.rb's next_order_seq), so the real seq Save
// assigns can differ if other items on the order also grow concurrently;
// this is a preview label only, corrected by the authoritative reload after
// Save.
function nextItemSeq(units) {
  const highest = (units || []).reduce((max, u) => Math.max(max, parseInt(u.seq, 10) || 0), 0);
  return highest + 1;
}

// A not-yet-created unit has no server row to seed UnitRow's normal
// buildDraft/persisted-value fallbacks from — this stands in for one. Every
// *_manual flag is false (nothing has been derived FOR it yet, let alone
// manually overridden), so UnitRow's existing splitIsDefault/weightIsDefault/
// dimIsDefault/locationIsDefault flags all read it as "showing a default"
// until the user types, exactly like a real non-manual unit would.
function buildAddedUnitSeed(seq) {
  return {
    id: undefined,
    seq,
    unit_label: `Unit ${seq}`,
    lpn_code: undefined,
    piece_split: "",
    piece_split_manual: false,
    booked_weight: "",
    booked_weight_uom: "lb",
    booked_weight_manual: false,
    booked_length: "",
    booked_width: "",
    booked_height: "",
    booked_dim_uom: "in",
    booked_dim_manual: false,
    reweigh_weight: "",
    reweigh_weight_uom: "lb",
    reweigh_length: "",
    reweigh_width: "",
    reweigh_height: "",
    reweigh_dim_uom: "in",
    storage_location: "",
    storage_location_manual: false,
    status: "Pending",
  };
}

// Per-item scannable-units table (mockup .scan-section/.scan-table) plus the
// Booked/Re-Weighed mode toggle above it. Selection state (`checkedIds`)
// lives one level up in ItemCard, per the plan, so Task 5's BulkApplyRow can
// reuse the exact same `{checkedIds, onToggleCheck, onToggleAll}` shape
// without this component needing to change.
//
// `preview` (Frontend Task 11): the live HUs-preview object computed by
// ItemCard via computeHandlingUnitsPreview above (one computation, shared by
// both the selection layer up there and the rendering here). Display-only:
// nothing here is stored, so reverting the draft or a Save+reload simply
// makes ItemCard recompute a "no preview" object and everything below
// renders exactly as before this task.
// Shared empty fallbacks (see EMPTY_UNITS_PREVIEW above for why a stable
// reference matters): a per-item pendingEdits prop that's simply "nothing
// pending yet" must never be a fresh {} each render, or every consumer
// downstream that happens to key off it would re-render pointlessly.
const EMPTY_ITEM_UNIT_EDITS = { existing: {}, added: {} };
const EMPTY_FIELD_EDITS = {};

const UnitsTable = ({
  item,
  units = [],
  mode,
  onModeChange,
  checkedIds,
  onToggleCheck,
  onToggleAll,
  readOnly = false,
  preview = EMPTY_UNITS_PREVIEW,
  // Editable-units fix: while ItemCard's deferSaves gate is open (this
  // item's draft is previewing something Save will recompute), every unit
  // row below — existing AND added — routes its edits into pendingEdits
  // instead of PATCHing immediately. `pendingEdits`/`onUnitPendingEdit` are
  // this item's slice of the container-owned map (ItemsMeasureUnits'
  // pendingUnitEdits) and its setter, threaded straight through from
  // ItemCard exactly like `preview` already is.
  deferSaves = false,
  pendingEdits = EMPTY_ITEM_UNIT_EDITS,
  onUnitPendingEdit = () => {},
  // Units-during-create fix: this table belongs to a pending "Add N Items"
  // card with no server-side item at all — `units` is always `[]`, every row
  // is a seed (isAdded) row, and there's no selection/bulk-apply/kebab
  // affordance possible since nothing has a real id yet. Threaded down to
  // every added UnitRow as `isNewItemSeed` for the stricter reduced chrome
  // (no checkbox, no status dropdown, "New unit"/"created on Save" labels —
  // see UnitRow) — the ordinary edit-flow's placeholder rows (an EXISTING
  // item's HUs draft growing) keep their current appearance untouched.
  isNewItemTable = false,
}) => {
  // A row's own updateUnit response is applied here immediately (so the
  // table feels instant) while the container's reload() — kicked off by the
  // same save, see UnitRow — refreshes the summary tiles and item rollups in
  // the background. Whenever the parent hands down a fresh `units` array
  // (i.e. once that reload lands), it supersedes this local copy, same
  // draft/baseline convergence pattern ItemCard uses for item fields.
  // handleUnitSaved only ever replaces a unit in place (same ids, same
  // length/order), so `preview` — computed by ItemCard from the prop `units`
  // — always agrees with this local copy on ids and count.
  const [localUnits, setLocalUnits] = useState(units);
  const headerCheckboxRef = useRef(null);
  // Scannable Units renders collapsed by default for EVERY item, new-item seed
  // tables included (user request: show it as a ">Scannable Units" disclosure)
  // — the chevron on the title toggles it open.
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    setLocalUnits(units);
  }, [units]);

  const handleUnitSaved = (updatedUnit) => {
    if (!updatedUnit) return;
    setLocalUnits((prev) => prev.map((u) => (u.id === updatedUnit.id ? updatedUnit : u)));
  };

  const { removedIds } = preview;

  // Selection math runs over the SELECTABLE units only — preview-removed
  // rows render forced-unchecked/disabled (UnitRow), ItemCard prunes their
  // ids out of checkedIds, and excluding them here keeps the header
  // select-all checked/indeterminate state agreeing with what the per-row
  // checkboxes actually show.
  const selectableUnits = useMemo(
    () => (removedIds.size > 0 ? localUnits.filter((u) => !removedIds.has(u.id)) : localUnits),
    [localUnits, removedIds]
  );
  const checkedCount = selectableUnits.filter((u) => checkedIds.has(u.id)).length;
  const allChecked = selectableUnits.length > 0 && checkedCount === selectableUnits.length;
  const indeterminate = checkedCount > 0 && !allChecked;

  // Belt-and-braces (on top of ItemCard's prune effect): the id list the
  // bulk row can PATCH never includes a preview-removed unit, even in the
  // render(s) between the user shrinking the HUs draft and the prune effect
  // committing — a row displayed as locked/"removed on Save" must never be
  // bulk-editable.
  const effectiveCheckedIds = useMemo(() => {
    if (removedIds.size === 0) return checkedIds;
    const next = new Set();
    checkedIds.forEach((id) => {
      if (!removedIds.has(id)) next.add(id);
    });
    return next;
  }, [checkedIds, removedIds]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, selectableUnits.length]);

  // Header count reflects what will exist after Save while the user is
  // actively changing HUs; otherwise (or once the field isn't dirty) it's
  // just the real row count, unchanged from before this task.
  const displayCount = preview.isDirty ? preview.displayCount : localUnits.length;

  return (
    <div className={`scan-section ${collapsed ? "collapsed" : ""}`}>
      <div className="scan-title-row">
        <button
          type="button"
          className="scan-title-toggle"
          aria-expanded={!collapsed}
          title={collapsed ? "Expand" : "Collapse"}
          onClick={() => setCollapsed((c) => !c)}
        >
          <span className="scan-chevron" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </span>
          <span className="scan-title">Scannable Units ({displayCount})</span>
        </button>
        {!collapsed && (
        <div className="scan-title-right">
          <div className="mode-toggle">
            <button
              type="button"
              className={`mode-btn ${mode !== "reweigh" ? "active" : ""}`}
              onClick={() => onModeChange("booked")}
            >
              Booked
            </button>
            <button
              type="button"
              className={`mode-btn ${mode === "reweigh" ? "active" : ""}`}
              // Re-Weigh records the ACTUAL measured weight after goods are
              // received, which can't happen while the order is still being
              // created — so it's disabled for not-yet-saved (new) items and
              // re-enables once the item/order is saved. The toggle stays on
              // Booked (its default) meanwhile.
              onClick={() => !isNewItemTable && onModeChange("reweigh")}
              disabled={isNewItemTable}
              title={isNewItemTable ? "Re-Weigh is available after the order is saved" : undefined}
            >
              Re-Weigh
            </button>
          </div>
        </div>
        )}
      </div>
      {!collapsed && (
      <div className="table-scroll">
        <table className="scan-table">
          <thead>
            <tr>
              <th>
                {/* Units-during-create fix: a pending item has no real units
                    to select/bulk-edit at all — the header checkbox is
                    simply omitted (empty cell, same column count) rather
                    than offered disabled. */}
                {!isNewItemTable && (
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="rowcheck"
                    checked={allChecked}
                    disabled={selectableUnits.length === 0}
                    onChange={() => onToggleAll(!allChecked)}
                  />
                )}
              </th>
              <th>Unit Label</th>
              <th>Piece Split</th>
              <th>Weight</th>
              <th>Dimensions (L&times;W&times;H)</th>
              <th>Storage Location</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {/* Mounts whenever something is selected — in read-only view too,
                where BulkApplyRow renders only its count/Clear/Download-labels
                area (no edit fields, no Apply). Any write path stays blocked:
                handleApply early-returns on readOnly and the edit inputs
                aren't rendered. Uses effectiveCheckedIds so a preview-removed
                unit can't be bulk-edited even before ItemCard's prune lands. */}
            {!isNewItemTable && effectiveCheckedIds.size > 0 && (
              <BulkApplyRow
                checkedIds={effectiveCheckedIds}
                mode={mode}
                readOnly={readOnly}
                onClear={() => onToggleAll(false)}
                deferSaves={deferSaves}
                onUnitPendingEdit={onUnitPendingEdit}
              />
            )}
            {localUnits.map((unit) => (
              <UnitRow
                key={unit.id}
                item={item}
                unit={unit}
                mode={mode}
                checked={effectiveCheckedIds.has(unit.id)}
                onCheck={onToggleCheck}
                onSaved={handleUnitSaved}
                readOnly={readOnly}
                previewRemoved={removedIds.has(unit.id)}
                preview={preview}
                deferSaves={deferSaves}
                pendingEdits={pendingEdits.existing[unit.id] || EMPTY_FIELD_EDITS}
                onPendingEdit={(fields) => onUnitPendingEdit("existing", unit.id, fields)}
              />
            ))}
            {/* HUs draft > persisted count: placeholder rows for the units
                the server will mint on Save. Editable-units fix: these are
                now real UnitRows in `isAdded` mode, pre-filled with their
                computed preview default (preview.newUnitPreviews[i]) but
                fully editable — see buildAddedUnitSeed/nextItemSeq above and
                UnitRow's isAdded branch for how the seed + pendingEdits +
                live preview combine into a displayed value. */}
            {Array.from({ length: preview.addCount }).map((_, i) => {
              const seq = nextItemSeq(localUnits) + i;
              return (
                <UnitRow
                  key={`added-${i}`}
                  item={item}
                  unit={buildAddedUnitSeed(seq)}
                  mode={mode}
                  checked={false}
                  onCheck={() => {}}
                  onSaved={() => {}}
                  readOnly={readOnly}
                  preview={preview}
                  isAdded
                  isNewItemSeed={isNewItemTable}
                  addedIndex={i}
                  deferSaves={deferSaves}
                  pendingEdits={pendingEdits.added[i] || EMPTY_FIELD_EDITS}
                  onPendingEdit={(fields) => onUnitPendingEdit("added", i, fields)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default UnitsTable;
